// =============================================================================
// Algolia custom hooks for Coolibar
//   0. Insights init (search-insights)
//   1. Config & state
//   2. Helpers
//   3. No-results UI (autocomplete dropdown)
//   4. Autocomplete hooks
//   5. InstantSearch hooks
//   6. Global listeners
// =============================================================================

// 0. Insights init -----------------------------------------------------------
// algoliaShopify installs window.aa late (after firing hooks.initialize), so
// we poll for it briefly and call init as soon as both aa and config exist.

function initInsights() {
  if (!window.aa || !window.algoliaShopify?.config) return false;
  const { app_id, search_api_key } = window.algoliaShopify.config;
  if (!app_id || !search_api_key) return false;
  window.aa("init", {
    appId: app_id,
    apiKey: search_api_key,
    useCookie: true,
  });
  return true;
}

if (!initInsights()) {
  document.addEventListener("algolia.hooks.initialize", initInsights);
  let attempts = 0;
  const poll = setInterval(() => {
    if (initInsights() || ++attempts > 50) clearInterval(poll);
  }, 100);
}

// 1. Config & state ----------------------------------------------------------

const collectionhandle = "top-sellers";
const productsLimits = 5;
let isLoading = false;

// 2. Helpers -----------------------------------------------------------------

function resizeShopifyImage(url, size) {
  if (!url) return url;
  return url.replace(/(\.[a-zA-Z]+)(\?|$)/, "_" + size + "$1$2");
}

function openFirstTwoFacets() {
  const checkboxes = document.querySelectorAll(".ais-dropdown-checkbox");
  [checkboxes[0], checkboxes[1]].forEach((cb) => {
    if (cb) cb.checked = true;
  });
}

function getBoostedVendor() {
  const url = window.location.href.toLowerCase();
  if (url.includes("suntect")) return "suntect";
  if (url.includes("mott50")) return "mott50";

  if (url.includes("/products/")) {
    const vendor = document
      .querySelector("[data-vendor]")
      ?.dataset.vendor?.toLowerCase();
    if (vendor === "suntect" || vendor === "mott50") return vendor;
  }

  return null;
}

// 3. No-results UI (autocomplete dropdown) -----------------------------------

const handleRenderNoResults = ({ render, html, state }, root) => {
  render(
    html` <div class="algolia-no-results_wrapper">
      <div class="algolia-no-results">
        <h2 class="algolia-no-results__title">
          We’re sorry, but there are no results for your search
          “<strong>${state.query}</strong>”.
        </h2>

        <h3 class="algolia-no-results__subtitle">Suggestions</h3>

        <ul class="algolia-no-results__suggestions">
          <li><a href="/collections/women">Women’s Sun Protection</a></li>
          <li><a href="/collections/men">Men’s Sun Protection</a></li>
          <li><a href="/collections/sun-hats">Sun Hats</a></li>
          <li><a href="/collections/swimwear">UV Swimwear</a></li>
        </ul>

        <div class="algolia-no-results__recomendations-wrapper">
          <h3 class="algolia-no-results__recommendations-title">
            Coolibar recommends
          </h3>
          <div class="algolia-no-results__products-wrapper"></div>
          <a
            class="algolia-no-results__shop-all--button"
            href="/collections/${collectionhandle}"
            >See All Recommended Products</a
          >
        </div>
      </div>
    </div>`,
    root,
  );
  productsRecommendations();
};

async function productsRecommendations() {
  if (isLoading) return;

  const container = document.querySelector(
    ".algolia-no-results__products-wrapper",
  );
  if (!container || container.children.length > 0) return;

  isLoading = true;
  try {
    const res = await fetch(
      `/collections/${collectionhandle}/products.json?limit=${productsLimits}`,
    );
    const { products } = await res.json();

    if (container.children.length > 0) return;

    products.forEach((product) => {
      const card = document.createElement("a");
      card.href = `/products/${product.handle}`;
      card.classList.add("algolia-no-results__product-card");

      const image = document.createElement("img");
      image.src = resizeShopifyImage(product.images[0]?.src, "400x600");
      image.alt = product.title;
      image.loading = "lazy";
      image.width = 400;
      image.height = 600;

      const title = document.createElement("h3");
      title.textContent = product.title;

      const price = document.createElement("p");
      price.classList.add("algolia-no-results__product-price");
      const priceValue = product.variants[0]?.price;
      price.textContent = priceValue ? `$${priceValue}` : "";

      card.appendChild(image);
      card.appendChild(title);
      card.appendChild(price);

      container.appendChild(card);
    });
  } finally {
    isLoading = false;
  }
}

// 4. Autocomplete hooks ------------------------------------------------------

document.addEventListener("algolia.hooks.initialize", () => {
  algoliaShopify.hooks.registerHook(
    "beforeAutocompletePluginOptions",
    (options, context) => {
      const vendor = getBoostedVendor();

      if (context.pluginName === "products" && vendor) {
        options.searchParameters.ruleContexts = [vendor];
      }
      return options;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteOptions",
    function (options) {
      options.openOnFocus = true;
      options.placeholder = "Search products";
      options.renderNoResults = handleRenderNoResults;

      return options;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteFiltersString",
    function (filters) {
      const exclusion = "NOT _tags:parent-style-split";
      if (!filters) return exclusion;
      return `(${filters}) AND ${exclusion}`;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteProductTemplate",
    function (
      defaultTemplate,
      templateOptions,
      distinct,
      itemLink,
      trackSearchAttribution,
    ) {
      const { html, item, components } = templateOptions;

      return html`
        <a
          href="${itemLink}"
          onClick="${() =>
            trackSearchAttribution && trackSearchAttribution(item)}"
        >
          <div class="aa-ItemContent">
            <div class="example-img">
              <img
                src="${resizeShopifyImage(item.image, "800x1200")}"
                alt="${item.title}"
                width="800"
                height="1200"
                loading="lazy"
              />
            </div>
            <div class="aa-ItemContentBody">
              <div class="aa-ItemContentTitle">
                ${components.Highlight({ hit: item, attribute: "title" })}
              </div>
              <div class="aa-pricing">
                $
                ${algoliaShopify.helpers?.formatMoney?.(
                  item.price_min ?? item.price,
                ) ??
                item.price_min ??
                item.price}
              </div>
            </div>
          </div>
        </a>
      `;
    },
  );
});

document.addEventListener("algolia.hooks.initialize", () => {
	algoliaShopify.hooks.registerHook(
		"beforeAutocompleteConfigurationOptions",
		(config, context) => {
			if (context.sourceId === "products") {
				config.queries = config.queries.map((query) => ({
					...query,
					params: {
						...query.params,
						analyticsTags: ["search-results"],
					},
				}));
			}
			return config;
		},
	);
});

// 5. InstantSearch hooks -----------------------------------------------------

document.addEventListener("algolia.hooks.initialize", function () {
  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchFiltersString",
    function (filters) {
      const exclusion = "NOT _tags:parent-style-split";
      if (!filters) return exclusion;
      return `(${filters}) AND ${exclusion}`;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchNoResultTemplate",
    function (data, html) {
      const params = new URLSearchParams(window.location.search);
      const query = params.get("q") || "";

      return html`<div class="ais-hit-empty">
        <h2>No products found</h2>

        ${query ? html`<p>No results for “<span>${query}</span>”.</p>` : ""}

        <div>
          <h3>Try these popular searches</h3>

          <ul class="algolia-no-results-page__suggestions">
            <li><a href="/collections/women">Women’s Sun Protection</a></li>
            <li><a href="/collections/men">Men’s Sun Protection</a></li>
            <li><a href="/collections/sun-hats">Sun Hats</a></li>
            <li><a href="/collections/swimwear">UV Swimwear</a></li>
          </ul>
        </div>
      </div>`;
    },
  );
});


document.addEventListener("algolia.hooks.initialize", () => {
  algoliaShopify.hooks.registerHook("beforeISStartAddWidgetArray", () => {
    const { configure } = window.algoliaShopify.externals.widgets;
    const isCollection = /\/collections\//i.test(window.location.pathname);
    return [
      configure({
        analyticsTags: [isCollection ? "collection" : "search-results"],
      }),
    ];
  });
});

// 6. Global listeners --------------------------------------------------------

window.addEventListener("load", openFirstTwoFacets);

document.addEventListener("click", (e) => {
  if (!e.target.matches(".aa-Panel")) return;
  const input = document.querySelector(".aa-Input");
  if (!input) return;
  input.dispatchEvent(
    new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
  );
  input.blur();
});